import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MdArrowBack,
  MdDescription,
  MdDownload,
  MdExpandLess,
  MdExpandMore,
  MdInfoOutline,
  MdOutlineCalendarToday,
  MdOutlineLocalShipping,
  MdRefresh,
  MdStar,
  MdStarBorder,
} from 'react-icons/md';
import { useAuth } from 'contexts/AuthContext';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { PageHeader, PageShell } from 'components/layout/PageShell';
import AiAssistantDrawer from 'domains/ai/components/AiAssistantDrawer';
import {
  UnifiedSurfaceCard,
  useUnifiedSurfaceStyles,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTabsStyles } from 'components/layout/useUnifiedUiStyles';
import { mapTemplateTitleToTranslation } from 'domains/shared/utils/categoryUtils';
import { useTranslation } from 'i18n/TranslationProvider';
import { translateRaw } from 'i18n/globalTranslator';
import Map from 'components/map/components/StaticMap';
import ListingItemsSection from 'domains/listing/components/ListingItemsSection';
import ListingImageGallery from 'domains/listing/components/ListingImageGallery';
import ListingLoaCard from 'domains/listing/components/ListingLoaCard';
import TransportPlanCard from 'domains/listing/components/TransportPlanCard';
import ListingMessagesPanel from 'domains/collaboration/components/ListingMessagesPanel';
import { getUnreadMessageCount } from 'domains/collaboration/services/collaborationService';
import useFetchApprovedTrucks from 'domains/truck/hooks/useFetchUserApprovedTrucks';
import useFetchDrivers from 'domains/truck/hooks/useFetchUserDrivers';
import useFetchUserTrailers from 'domains/trailer/hooks/useFetchUserTrailers';
import {
  acceptFixedPriceListing,
  getListingById,
  getQuotesForListing,
  planListingTransport,
  releaseListing,
  submitQuote,
  toggleWatch,
  updateQuote,
  updateListingStatus,
  withdrawQuote,
} from 'domains/listing/services/listingService';
import StatusBadge from '../components/StatusBadge';
import DocumentUploader from 'components/documents/DocumentUploader';
import DocumentList from 'components/documents/DocumentList';
import API from 'api/api';



function fmt(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateRange(from, to) {
  if (!from && !to) return translateRaw('Flexible dates');
  if (from && to && from !== to) return `${fmt(from)} – ${fmt(to)}`;
  return fmt(from || to);
}

function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000);
}

function formatDistanceKm(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${numeric} ${translateRaw('booking.km')}` : null;
}

function formatMoney(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `EUR ${numeric.toFixed(2)}` : null;
}

function toDateInputValue(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function isQuoteEditable(status) {
  return ['pending', 'rejected', 'expired', 'withdrawn'].includes((status || '').toLowerCase());
}

function QuotePanel({ listing, listingId, myQuote, onQuoteChanged }) {
  const toast = useToast();
  const { t } = useTranslation();
  const { data: approvedTrucks, loading: loadingTrucks } = useFetchApprovedTrucks();
  const [netPrice, setNetPrice] = useState(myQuote?.netPrice ?? '');
  const [vatRate, setVatRate] = useState(myQuote?.vatRate ?? 20);
  const [description, setDescription] = useState(myQuote?.description ?? '');
  const [minNetPrice, setMinNetPrice] = useState(myQuote?.minNetPrice ?? '');
  const [proposedPickupDate, setProposedPickupDate] = useState(toDateInputValue(myQuote?.proposedPickupDate));
  const [proposedDeliveryDate, setProposedDeliveryDate] = useState(toDateInputValue(myQuote?.proposedDeliveryDate));
  const [quoteExpiresAt, setQuoteExpiresAt] = useState(toDateInputValue(myQuote?.quoteExpiresAt));
  const [sharesTracking, setSharesTracking] = useState(Boolean(myQuote?.sharesTracking));
  const [vehicleTruckId, setVehicleTruckId] = useState(myQuote?.vehicleTruckId ?? '');
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [isEditing, setIsEditing] = useState(!myQuote || isQuoteEditable(myQuote?.status));
  
  useEffect(() => {
    setNetPrice(myQuote?.netPrice ?? '');
    setVatRate(myQuote?.vatRate ?? 20);
    setDescription(myQuote?.description ?? '');
    setMinNetPrice(myQuote?.minNetPrice ?? '');
    setProposedPickupDate(toDateInputValue(myQuote?.proposedPickupDate));
    setProposedDeliveryDate(toDateInputValue(myQuote?.proposedDeliveryDate));
    setQuoteExpiresAt(toDateInputValue(myQuote?.quoteExpiresAt));
    setSharesTracking(Boolean(myQuote?.sharesTracking));
    setVehicleTruckId(myQuote?.vehicleTruckId ?? '');
    setIsEditing(!myQuote || isQuoteEditable(myQuote?.status));
  }, [myQuote]);

  const { panelBorder, mutedText, subtleBg, subtleBorder } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const hoverBg = useColorModeValue('blue.100', 'whiteAlpha.200');
  const budgetAccent = 'blue.500';
  const clientPriceLabel = formatMoney(listing?.clientPriceGross);

  const grossPrice =
    netPrice && !Number.isNaN(Number(netPrice))
      ? (Number(netPrice) * (1 + Number(vatRate) / 100)).toFixed(2)
      : null;
  const quoteDelta = grossPrice && clientPriceLabel
    ? Number(grossPrice) - Number(listing.clientPriceGross)
    : null;

  const quoteStatus = (myQuote?.status || '').toLowerCase();
  const canUpdatePendingQuote = quoteStatus === 'pending';
  const canCreateRevision = myQuote && ['rejected', 'expired', 'withdrawn'].includes(quoteStatus);
  const canMutateQuote = !myQuote || canUpdatePendingQuote || canCreateRevision;
  const pickupMin = toDateInputValue(listing?.pickupDateFrom);
  const pickupMax = toDateInputValue(listing?.pickupDateTo || listing?.pickupDateFrom);
  const deliveryMin = toDateInputValue(listing?.deliveryDateFrom || proposedPickupDate);
  const deliveryMax = toDateInputValue(listing?.deliveryDateTo || listing?.deliveryDateFrom);

  const handleSubmit = async () => {
    if (!netPrice || Number(netPrice) <= 0) {
      toast({ title: t('Enter a valid net price'), status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    const payload = {
      netPrice: Number(netPrice),
      vatRate: Number(vatRate),
      description,
      minNetPrice: minNetPrice ? Number(minNetPrice) : undefined,
      proposedPickupDate: proposedPickupDate || undefined,
      proposedDeliveryDate: proposedDeliveryDate || undefined,
      quoteExpiresAt: quoteExpiresAt || undefined,
      sharesTracking,
      vehicleTruckId: vehicleTruckId || undefined,
    };

    setSubmitting(true);
    try {
      if (canUpdatePendingQuote) {
        await updateQuote(listingId, myQuote.id || myQuote._id, payload);
      } else {
        await submitQuote(listingId, {
          ...payload,
          previousQuoteId: canCreateRevision ? myQuote.id || myQuote._id : undefined,
        });
      }
      toast({
        title: canUpdatePendingQuote ? t('Quote updated') : t('Quote submitted'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      setIsEditing(false);
      onQuoteChanged();
    } catch (error) {
      toast({
        title: error?.response?.data?.message || t('Failed to save quote'),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    const quoteId = myQuote?._id || myQuote?.id;
    if (!quoteId) return;

    setWithdrawing(true);
    try {
      await withdrawQuote(listingId, quoteId);
      toast({ title: t('Quote withdrawn'), status: 'info', duration: 2500, isClosable: true });
      onQuoteChanged();
    } catch {
      toast({ title: t('Failed to withdraw quote'), status: 'error', duration: 3000, isClosable: true });
    } finally {
      setWithdrawing(false);
    }
  };

  if (myQuote && !isEditing) {
    return (
      <UnifiedSurfaceCard p={6}>
        <HStack justify="space-between" align="flex-start" mb={5}>
          <Box>
            <Text fontSize="2xl" lineHeight="1" fontWeight="900" color={titleColor}>
              {t('My Quote')}
            </Text>
            <Text fontSize="sm" color={mutedText} mt={1}>
              {t('Review the offer currently visible to the client.')}
            </Text>
          </Box>
          <Badge
            colorScheme={quoteStatus === 'accepted' ? 'green' : quoteStatus === 'pending' ? 'blue' : 'gray'}
            borderRadius="full"
            px={3}
            py={1}
            textTransform="capitalize"
          >
            {quoteStatus || 'pending'}
          </Badge>
        </HStack>

        <VStack align="stretch" spacing={4}>
          {clientPriceLabel ? (
            <Box
              px={4}
              py={4}
              borderRadius="20px"
              bg={subtleBg}
              border="1px solid"
              borderColor={subtleBorder}
            >
              <Text fontSize="10px" fontWeight="800" color={budgetAccent} textTransform="uppercase" letterSpacing="0.08em">
                {t('Client target price')}
              </Text>
              <Text fontSize="2xl" fontWeight="900" color={titleColor} mt={1}>
                {clientPriceLabel}
              </Text>
            </Box>
          ) : null}

          <Grid templateColumns="1fr 120px" gap={3}>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Net quote')}
              </Text>
              <Box
                px={4}
                py={3}
                borderRadius="18px"
                bg="whiteAlpha.500"
                border="1px solid"
                borderColor={panelBorder}
              >
                <Text fontWeight="800" color={titleColor}>
                  €{Number(myQuote.netPrice || 0).toFixed(2)}
                </Text>
              </Box>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('VAT rate')}
              </Text>
              <Box
                px={4}
                py={3}
                borderRadius="18px"
                bg="whiteAlpha.500"
                border="1px solid"
                borderColor={panelBorder}
              >
                <Text fontWeight="800" color={titleColor}>
                  {myQuote.vatRate ?? 20}%
                </Text>
              </Box>
            </Box>
          </Grid>

          <Box>
            <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
              {t('Quote description')}
            </Text>
            <Box
              px={4}
              py={3}
              minH="120px"
              borderRadius="20px"
              bg="whiteAlpha.500"
              border="1px solid"
              borderColor={panelBorder}
            >
              <Text fontSize="sm" color={myQuote.description ? titleColor : mutedText}>
                {myQuote.description || t('No description added.')}
              </Text>
            </Box>
          </Box>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Minimum quote (net)')}
              </Text>
              <Box px={4} py={3} borderRadius="18px" bg="whiteAlpha.500" border="1px solid" borderColor={panelBorder}>
                <Text fontWeight="700" color={titleColor}>
                  {myQuote.minNetPrice != null ? `€${Number(myQuote.minNetPrice).toFixed(2)}` : t('Not set')}
                </Text>
              </Box>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Quote expiry date')}
              </Text>
              <Box px={4} py={3} borderRadius="18px" bg="whiteAlpha.500" border="1px solid" borderColor={panelBorder}>
                <Text fontWeight="700" color={titleColor}>
                  {myQuote.quoteExpiresAt ? fmt(myQuote.quoteExpiresAt) : t('No expiry')}
                </Text>
              </Box>
            </Box>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Pick-up date')}
              </Text>
              <Box px={4} py={3} borderRadius="18px" bg="whiteAlpha.500" border="1px solid" borderColor={panelBorder}>
                <Text fontWeight="700" color={titleColor}>
                  {myQuote.proposedPickupDate ? fmt(myQuote.proposedPickupDate) : t('Flexible / not set')}
                </Text>
              </Box>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Delivery date')}
              </Text>
              <Box px={4} py={3} borderRadius="18px" bg="whiteAlpha.500" border="1px solid" borderColor={panelBorder}>
                <Text fontWeight="700" color={titleColor}>
                  {myQuote.proposedDeliveryDate ? fmt(myQuote.proposedDeliveryDate) : t('Flexible / not set')}
                </Text>
              </Box>
            </Box>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Vehicle type')}
              </Text>
              <Box px={4} py={3} borderRadius="18px" bg="whiteAlpha.500" border="1px solid" borderColor={panelBorder}>
                <Text fontWeight="700" color={titleColor}>
                  {myQuote.vehicleLabel || t('Not selected')}
                </Text>
              </Box>
            </Box>
            <Box>
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
                {t('Tracking visibility')}
              </Text>
              <Box px={4} py={3} borderRadius="18px" bg="whiteAlpha.500" border="1px solid" borderColor={panelBorder}>
                <Text fontWeight="700" color={titleColor}>
                  {myQuote.sharesTracking ? t('Shipment tracking will be shared') : t('Tracking not promised')}
                </Text>
              </Box>
            </Box>
          </Grid>
        </VStack>

        {(canMutateQuote || quoteStatus === 'pending') ? (
          <HStack mt={5} spacing={3}>
            {canMutateQuote ? (
              <Button
                flex="1"
                variant="outline"
                colorScheme="blue"
                borderRadius="14px"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                {canUpdatePendingQuote ? t('Edit Quote') : t('Send Revision')}
              </Button>
            ) : null}
            {quoteStatus === 'pending' ? (
              <Button
                flex="1"
                variant="outline"
                colorScheme="red"
                borderRadius="14px"
                size="sm"
                isLoading={withdrawing}
                onClick={handleWithdraw}
              >
                {t('Withdraw')}
              </Button>
            ) : null}
          </HStack>
        ) : null}
      </UnifiedSurfaceCard>
    );
  }

  return (
    <UnifiedSurfaceCard p={6}>
      <Text fontSize="2xl" lineHeight="1" fontWeight="900" mb={2} color={titleColor}>
        {t('Submit Quote')}
      </Text>
      <Text fontSize="sm" color={mutedText} mb={5}>
        {t('Keep the offer tight and clear. The client will see pricing, message, dates, vehicle choice, and tracking commitment.')}
      </Text>

      <VStack align="stretch" spacing={4}>
        {clientPriceLabel ? (
          <Box
            px={4}
            py={4}
            borderRadius="20px"
            bg={subtleBg}
            border="1px solid"
            borderColor={subtleBorder}
          >
            <Text fontSize="10px" fontWeight="800" color={budgetAccent} textTransform="uppercase" letterSpacing="0.08em">
              {t('Client target price')}
            </Text>
            <Text fontSize="2xl" fontWeight="900" color={titleColor} mt={1}>
              {clientPriceLabel}
            </Text>
            <Text fontSize="xs" color={mutedText} mt={1}>
              {t('Visible to you while quoting. You can still send your own net offer below or above this amount.')}
            </Text>
          </Box>
        ) : null}

        <Grid templateColumns="1fr 120px" gap={3}>
          <Box>
            <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
              {t('Your net quote')}
            </Text>
            <InputGroup size="md">
              <Input
                type="number"
                min={0}
                value={netPrice}
                onChange={(e) => setNetPrice(e.target.value)}
                placeholder={t('Enter net quote')}
                bg="whiteAlpha.500"
                borderColor={panelBorder}
                borderRadius="18px"
                focusBorderColor="blue.400"
              />
              <InputRightAddon bg="blue.500" color="white" fontWeight="700" borderRadius="0 18px 18px 0">
                €
              </InputRightAddon>
            </InputGroup>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
              {t('VAT rate')}
            </Text>
            <Select
              size="md"
              value={vatRate}
              onChange={(e) => setVatRate(Number(e.target.value))}
              bg="whiteAlpha.500"
              borderColor={panelBorder}
              borderRadius="18px"
              focusBorderColor="blue.400"
            >
              {[0, 5, 7, 10, 12, 19, 20, 21, 23, 25].map((value) => (
                <option key={value} value={value}>{value}%</option>
              ))}
            </Select>
          </Box>
        </Grid>

        <Box
          px={4}
          py={3}
          borderRadius="18px"
          bg={subtleBg}
          border="1px solid"
          borderColor={subtleBorder}
        >
          <Text fontSize="xs" fontWeight="800" color={labelColor} mb={1}>
            {t('Total gross price (paid by client)')}
          </Text>
          <Text fontSize="lg" fontWeight="900" color={grossPrice ? budgetAccent : mutedText}>
            {grossPrice ? `€${grossPrice}` : t('Enter a net quote')}
          </Text>
          {quoteDelta != null ? (
            <Text fontSize="xs" color={quoteDelta === 0 ? mutedText : quoteDelta > 0 ? 'orange.500' : 'green.500'} mt={1}>
              {quoteDelta === 0
                ? t('Matches the client target price')
                : quoteDelta > 0
                  ? t('{{amount}} above the client target', { amount: formatMoney(Math.abs(quoteDelta)) })
                  : t('{{amount}} below the client target', { amount: formatMoney(Math.abs(quoteDelta)) })}
            </Text>
          ) : null}
        </Box>

        <Box>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="xs" fontWeight="800" color={labelColor}>
              {t('Quote description')}
            </Text>
            <Text fontSize="xs" color={mutedText}>{description.length}/1000</Text>
          </HStack>
          <Textarea
            size="md"
            rows={5}
            maxLength={1000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('You can add information about your quote here, e.g. cargo insurance, route planning, or loading approach.')}
            bg="whiteAlpha.500"
            borderColor={panelBorder}
            borderRadius="20px"
            focusBorderColor="blue.400"
          />
          <Text fontSize="xs" color={mutedText} mt={2}>
            {t('Do not include your contact information.')}
          </Text>
        </Box>

        <Box>
          <HStack mb={1}>
            <Text fontSize="xs" fontWeight="800" color={labelColor}>
              {t('Minimum quote (net)')}
            </Text>
            <Tooltip label={t('The lowest net price you would still accept for this shipment.')}>
              <Box>
                <Icon as={MdInfoOutline} boxSize={3.5} color={mutedText} cursor="help" />
              </Box>
            </Tooltip>
          </HStack>
          <InputGroup size="md">
            <Input
              type="number"
              min={0}
              value={minNetPrice}
              onChange={(e) => setMinNetPrice(e.target.value)}
              placeholder={t('Enter net quote')}
              bg="whiteAlpha.500"
              borderColor={panelBorder}
              borderRadius="18px"
              focusBorderColor="blue.400"
            />
            <InputRightAddon bg="whiteAlpha.400" borderRadius="0 18px 18px 0">€</InputRightAddon>
          </InputGroup>
        </Box>

        <Box>
          <Button
            size="sm"
            onClick={() => setShowDetails((v) => !v)}
            variant="ghost"
            bg={subtleBg}
            color={titleColor}
            px={4}
            borderRadius="full"
            leftIcon={<Icon as={showDetails ? MdExpandLess : MdExpandMore} />}
            _hover={{ bg: hoverBg }}
            fontWeight="800"
            fontSize="sm"
          >
            {showDetails ? t('Hide details') : t('Show details')}
          </Button>
        </Box>

        {showDetails ? (
          <VStack align="stretch" spacing={4}>
            <Box>
              <HStack mb={1}>
                <Icon as={MdOutlineCalendarToday} boxSize={4} color={mutedText} />
                <Text fontSize="xs" fontWeight="800" color={labelColor}>
                  {t('Pick-up date')}
                </Text>
              </HStack>
              <Input
                type="date"
                value={proposedPickupDate}
                onChange={(e) => setProposedPickupDate(e.target.value)}
                min={pickupMin || undefined}
                max={pickupMax || undefined}
                bg="whiteAlpha.500"
                borderColor={panelBorder}
                borderRadius="18px"
                focusBorderColor="blue.400"
              />
            </Box>

            <Box>
              <HStack mb={1}>
                <Icon as={MdOutlineCalendarToday} boxSize={4} color={mutedText} />
                <Text fontSize="xs" fontWeight="800" color={labelColor}>
                  {t('Delivery date')}
                </Text>
              </HStack>
              <Input
                type="date"
                value={proposedDeliveryDate}
                onChange={(e) => setProposedDeliveryDate(e.target.value)}
                min={deliveryMin || proposedPickupDate || undefined}
                max={deliveryMax || undefined}
                bg="whiteAlpha.500"
                borderColor={panelBorder}
                borderRadius="18px"
                focusBorderColor="blue.400"
              />
            </Box>

            <Box>
              <HStack mb={1}>
                <Icon as={MdOutlineCalendarToday} boxSize={4} color={mutedText} />
                <Text fontSize="xs" fontWeight="800" color={labelColor}>
                  {t('Quote expiry date')}
                </Text>
              </HStack>
              <Input
                type="date"
                value={quoteExpiresAt}
                onChange={(e) => setQuoteExpiresAt(e.target.value)}
                min={toDateInputValue(new Date())}
                bg="whiteAlpha.500"
                borderColor={panelBorder}
                borderRadius="18px"
                focusBorderColor="blue.400"
              />
            </Box>

            <Box>
              <HStack mb={1}>
                <Icon as={MdOutlineLocalShipping} boxSize={4} color={mutedText} />
                <Text fontSize="xs" fontWeight="800" color={labelColor}>
                  {t('Vehicle type')}
                </Text>
              </HStack>
              <Select
                value={vehicleTruckId}
                onChange={(e) => setVehicleTruckId(e.target.value)}
                bg="whiteAlpha.500"
                borderColor={panelBorder}
                borderRadius="18px"
                focusBorderColor="blue.400"
                isDisabled={loadingTrucks}
              >
                <option value="">
                  {loadingTrucks ? t('Loading fleet...') : t('Choose from fleet')}
                </option>
                {approvedTrucks.map((truck) => {
                  const optionLabel = [truck.licensePlate, truck.carCapacity ? `${truck.carCapacity} cars` : null, truck.maxLoadKg ? `${truck.maxLoadKg} kg` : null]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <option key={truck._id} value={truck._id}>
                      {optionLabel || truck.licensePlate}
                    </option>
                  );
                })}
              </Select>
            </Box>

            <Box
              px={4}
              py={3}
              borderRadius="18px"
              bg="whiteAlpha.300"
              border="1px solid"
              borderColor={panelBorder}
            >
              <Checkbox
                isChecked={sharesTracking}
                onChange={(e) => setSharesTracking(e.target.checked)}
                colorScheme="blue"
              >
                <Text fontSize="sm" color={titleColor}>
                  {t("I'm going to share shipment tracking information")}
                </Text>
              </Checkbox>
            </Box>

            <Box
              px={4}
              py={4}
              borderRadius="20px"
              bg="whiteAlpha.300"
              border="1px dashed"
              borderColor={panelBorder}
              opacity={0.8}
            >
              <Text fontSize="xs" fontWeight="800" color={labelColor} mb={2}>
                {t('Add quote to group')}
              </Text>
              <Select isDisabled bg="whiteAlpha.500" borderColor={panelBorder} borderRadius="18px">
                <option>{t('Coming soon')}</option>
              </Select>
              <Checkbox mt={3} isDisabled colorScheme="blue">
                <Text fontSize="sm" color={mutedText}>
                  {t('Use the information in future quotes')}
                </Text>
              </Checkbox>
            </Box>
          </VStack>
        ) : null}

        <Button
          colorScheme="blue"
          borderRadius="18px"
          fontWeight="800"
          h="48px"
          isLoading={submitting}
          onClick={handleSubmit}
        >
          {canUpdatePendingQuote ? t('Update Quote') : canCreateRevision ? t('Send Revision') : t('Confirm Quote')}
        </Button>
        <Text fontSize="xs" color={mutedText} lineHeight="1.5">
          {t('The gross price is what the customer pays. Our system calculates VAT based on your set rate.')}
        </Text>
      </VStack>
    </UnifiedSurfaceCard>
  );
}

function FixedPriceAcceptPanel({ listing, onAccepted }) {
  const toast = useToast();
  const { t } = useTranslation();
  const { mutedText, panelBorder, subtleBg, subtleBorder } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');
  const [accepting, setAccepting] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const fixedPrice = formatMoney(listing.clientPriceGross);
  const isUnavailable = ['accepted', 'awaiting_payment', 'paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'].includes(String(listing.status || '').toLowerCase());

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await acceptFixedPriceListing(listing._id || listing.id);
      toast({
        title: t('Listing accepted'),
        description: t('You are now the assigned partner for this fixed-price listing.'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onAccepted();
    } catch (error) {
      toast({
        title: error?.response?.data?.message || t('Failed to accept listing'),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setAccepting(false);
    }
  };

  return (
    <UnifiedSurfaceCard p={6}>
      <Text fontSize="2xl" lineHeight="1" fontWeight="900" mb={2} color={titleColor}>
        {t('Fixed Price Listing')}
      </Text>
      <Text fontSize="sm" color={mutedText} mb={5}>
        {t('Quotes are disabled for this shipment. If you accept it, the client price becomes an accepted booking immediately.')}
      </Text>

      <Box
        px={4}
        py={4}
        borderRadius="20px"
        bg={subtleBg}
        border="1px solid"
        borderColor={subtleBorder}
        mb={4}
      >
        <Text fontSize="10px" fontWeight="800" color={mutedText} textTransform="uppercase" letterSpacing="0.08em">
          {t('Client fixed price')}
        </Text>
        <Text fontSize="2xl" fontWeight="900" color="blue.500" mt={1}>
          {fixedPrice || t('Not available')}
        </Text>
      </Box>

      <Box
        px={4}
        py={4}
        borderRadius="20px"
        bg="whiteAlpha.500"
        border="1px solid"
        borderColor={panelBorder}
      >
        <Checkbox
          isChecked={hasAcceptedTerms}
          onChange={(event) => setHasAcceptedTerms(event.target.checked)}
          colorScheme="blue"
          isDisabled={isUnavailable}
        >
          <Text fontSize="sm">
            {t('I understand that accepting this listing assigns it to me at the fixed client price shown above.')}
          </Text>
        </Checkbox>
      </Box>

      <Button
        mt={5}
        colorScheme="blue"
        borderRadius="18px"
        fontWeight="800"
        h="48px"
        isLoading={accepting}
        isDisabled={!fixedPrice || !hasAcceptedTerms || isUnavailable}
        onClick={handleAccept}
      >
        {isUnavailable ? t('Listing unavailable') : t('Accept Listing')}
      </Button>
    </UnifiedSurfaceCard>
  );
}

function QuotesTab({ listingId }) {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { mutedText } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');

  useEffect(() => {
    getQuotesForListing(listingId)
      .then((data) => setQuotes(Array.isArray(data) ? data : data?.quotes ?? []))
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  }, [listingId]);

  if (loading) {
    return (
      <Flex justify="center" py={12}>
        <Spinner color="blue.400" />
      </Flex>
    );
  }

  if (quotes.length === 0) {
    return (
      <UnifiedSurfaceCard p={6}>
        <Text fontSize="sm" color={mutedText}>
          {t('No quotes submitted yet for this listing.')}
        </Text>
      </UnifiedSurfaceCard>
    );
  }

  return (
    <Stack spacing={4}>
      {quotes.map((quote, index) => {
        const quoteNet = quote.netPrice ?? quote._netPrice;
        const quoteVat = quote.vatRate ?? quote._vatRate;
        const grossPrice = quoteNet && quoteVat != null
          ? (quoteNet * (1 + quoteVat / 100)).toFixed(2)
          : quote.grossPrice ?? quote._grossPrice ?? null;

        return (
          <UnifiedSurfaceCard
            key={quote._id || quote.id || index}
            p={5}
          >
            <HStack justify="space-between" align="flex-start" mb={2}>
              <Box>
                <Text fontWeight="800" color={titleColor}>
                  {t('Quote #{{count}}', { count: index + 1 })}
                </Text>
                <Text fontSize="xs" color={mutedText} textTransform="capitalize">
                  {quote.status || 'pending'}
                </Text>
              </Box>
              <Text fontSize="lg" fontWeight="800" color="blue.500">
                {grossPrice ? `€${grossPrice}` : '—'}
              </Text>
            </HStack>
            {quote.description ? (
              <Text fontSize="sm" color={mutedText}>
                {quote.description}
              </Text>
            ) : null}
          </UnifiedSurfaceCard>
        );
      })}
    </Stack>
  );
}


function TransportCard({ listing, onStatusChanged }) {
  const toast = useToast();
  const { t } = useTranslation();
  const [marking, setMarking] = useState(false);
  const { mutedText } = useUnifiedSurfaceStyles();
  const { data: approvedTrucks, loading: loadingTrucks } = useFetchApprovedTrucks();
  const { data: drivers, loading: loadingDrivers } = useFetchDrivers();
  const { data: trailers, loading: loadingTrailers } = useFetchUserTrailers();
  const titleColor = useColorModeValue('navy.800', 'white');

  const listingId = listing._id || listing.id;
  const {
    isOpen: isPlanOpen,
    onOpen: openPlan,
    onClose: closePlan,
  } = useDisclosure();
  const { isOpen: isPickupOpen, onOpen: openPickup, onClose: closePickup } = useDisclosure();
  const { isOpen: isDeliveredOpen, onOpen: openDelivered, onClose: closeDelivered } = useDisclosure();
  const pickupUploaderRef = useRef(null);
  const cmrUploaderRef = useRef(null);
  const deliveryUploaderRef = useRef(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [planTruckId, setPlanTruckId] = useState(listing?.transportPlan?.truck?.id || listing?.assignedTruck || '');
  const [planDriverId, setPlanDriverId] = useState(listing?.transportPlan?.driver?.id || listing?.assignedDriver || '');
  const [planTrailerId, setPlanTrailerId] = useState(listing?.transportPlan?.trailer?.id || listing?.assignedTrailer || '');
  const [planPickupDate, setPlanPickupDate] = useState(toDateInputValue(listing?.transportPlan?.pickupDate || listing?.partnerPickupDate));
  const [planDeliveryDate, setPlanDeliveryDate] = useState(toDateInputValue(listing?.transportPlan?.deliveryDate || listing?.partnerDeliveryDate));
  const [purchaseTermsDoc, setPurchaseTermsDoc] = useState(null);
  const [purchaseTermsLoading, setPurchaseTermsLoading] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const fetchPurchaseTerms = useCallback(async () => {
    setPurchaseTermsLoading(true);
    try {
      const res = await API.get('/documents/purchase-terms');
      setPurchaseTermsDoc(res?.data?.data || null);
    } catch {
      setPurchaseTermsDoc(null);
    } finally {
      setPurchaseTermsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPlanOpen) {
      fetchPurchaseTerms();
      setHasAcceptedTerms(false);
    }
  }, [isPlanOpen, fetchPurchaseTerms]);

  useEffect(() => {
    setPlanTruckId(listing?.transportPlan?.truck?.id || listing?.assignedTruck || '');
    setPlanDriverId(listing?.transportPlan?.driver?.id || listing?.assignedDriver || '');
    setPlanTrailerId(listing?.transportPlan?.trailer?.id || listing?.assignedTrailer || '');
    setPlanPickupDate(toDateInputValue(listing?.transportPlan?.pickupDate || listing?.partnerPickupDate));
    setPlanDeliveryDate(toDateInputValue(listing?.transportPlan?.deliveryDate || listing?.partnerDeliveryDate));
  }, [listing]);

  const today = toDateInputValue(new Date());
  const planPickupMin = (listing?.pickupDateFrom && toDateInputValue(listing.pickupDateFrom) > today)
    ? toDateInputValue(listing.pickupDateFrom)
    : today;
  const planPickupMax = toDateInputValue(listing?.pickupDateTo || undefined);

  const planDeliveryMinBound = (listing?.deliveryDateFrom && toDateInputValue(listing.deliveryDateFrom) > today)
    ? toDateInputValue(listing.deliveryDateFrom)
    : today;
  const planDeliveryMin = planPickupDate && planPickupDate > planDeliveryMinBound
    ? planPickupDate
    : planDeliveryMinBound;
  const planDeliveryMax = toDateInputValue(listing?.deliveryDateTo || undefined);

  const handleMark = async (newStatus) => {
    setMarking(true);
    try {
      await updateListingStatus(listingId, newStatus);
      toast({
        title: t('Status updated to {{status}}', { status: newStatus }),
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onStatusChanged();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || t('Failed to update status'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setMarking(false);
    }
  };

  const handlePlanTransport = async () => {
    if (!planTruckId || !planDriverId || !planPickupDate || !planDeliveryDate) {
      toast({
        title: t('Select a truck, driver, pickup date, and delivery date.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setMarking(true);
    try {
      await planListingTransport(listingId, {
        truckId: planTruckId,
        driverId: planDriverId,
        trailerId: planTrailerId || null,
        pickupDate: planPickupDate,
        deliveryDate: planDeliveryDate,
      });
      toast({
        title: listing.status === 'planned' || listing.status === 'in_transit' ? t('Transport plan updated') : t('Transport plan confirmed'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      closePlan();
      onStatusChanged();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || t('Failed to confirm transport plan'),
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setMarking(false);
    }
  };

  const confirmPickup = async () => {
    const result = await pickupUploaderRef.current?.upload();
    if (!result?.success) return;
    await handleMark('pickedUp');
    setRefreshTrigger((value) => value + 1);
    closePickup();
  };

  const confirmDelivered = async () => {
    const cmrResult = cmrUploaderRef.current?.hasFiles?.() ? await cmrUploaderRef.current.upload() : null;
    const deliveryResult = deliveryUploaderRef.current?.hasFiles?.() ? await deliveryUploaderRef.current.upload() : null;
    if (!cmrResult?.success && !deliveryResult?.success) {
      toast({
        title: t('Upload a CMR or delivery photos before marking delivered.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    await handleMark('delivered');
    setRefreshTrigger((value) => value + 1);
    closeDelivered();
  };

  return (
    <UnifiedSurfaceCard p={5}>
      <Text fontSize="md" fontWeight="800" mb={4} color={titleColor}>
        {t('Transport Progress')}
      </Text>
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between">
          <Text fontSize="sm" color={mutedText}>{t('Stage')}</Text>
          <StatusBadge status={listing.status} />
        </HStack>
        {listing.paidAt && (
          <HStack justify="space-between">
            <Text fontSize="sm" color={mutedText}>{t('Payment confirmed')}</Text>
            <Text fontSize="sm" fontWeight="700" color={titleColor}>{fmt(listing.paidAt)}</Text>
          </HStack>
        )}
        {listing.pickedUpAt && (
          <HStack justify="space-between">
            <Text fontSize="sm" color={mutedText}>{t('Picked up')}</Text>
            <Text fontSize="sm" fontWeight="700" color={titleColor}>{fmt(listing.pickedUpAt)}</Text>
          </HStack>
        )}
        {listing.deliveredAt && (
          <HStack justify="space-between">
            <Text fontSize="sm" color={mutedText}>{t('Delivered')}</Text>
            <Text fontSize="sm" fontWeight="700" color={titleColor}>{fmt(listing.deliveredAt)}</Text>
          </HStack>
        )}
      </VStack>

      {listing.status === 'accepted' && (
        <Text fontSize="xs" color={mutedText} mt={4}>
          {t('Your quote has been accepted. Awaiting payment confirmation from the admin before you can begin the job.')}
        </Text>
      )}
      {listing.status === 'paid' && (
        <>
          <Text fontSize="xs" color={mutedText} mt={4} mb={3}>
            {t('Payment confirmed. Confirm the assigned fleet and planned dates before the listing moves to Planned.')}
          </Text>
          <Button colorScheme="blue" borderRadius="xl" isLoading={marking} onClick={openPlan}>
            {t('Plan Transport')}
          </Button>
        </>
      )}
      {(listing.status === 'planned' || listing.status === 'in_transit') && (
        <>
          <Button mt={5} colorScheme="blue" borderRadius="xl" onClick={openPlan}>
            {t('Update Transport Plan')}
          </Button>
          <Button mt={3} colorScheme="blue" borderRadius="xl" isLoading={marking} onClick={openPickup}>
            {t('Mark Picked Up')}
          </Button>
        </>
      )}
      {listing.status === 'pickedUp' && (
        <Button mt={5} colorScheme="green" borderRadius="xl" isLoading={marking} onClick={openDelivered}>
          {t('Upload Final Docs & Mark Delivered')}
        </Button>
      )}
      {(listing.status === 'delivered' || listing.status === 'completed') && (
        <Text mt={5} fontSize="sm" fontWeight="700" color="green.500">
          ✓ {listing.status === 'completed' ? t('Transport completed.') : t('Goods delivered — awaiting client confirmation.')}
        </Text>
      )}

      <Box mt={5}>
        <TransportPlanCard
          transportPlan={listing.transportPlan}
          title={t('Confirmed Transport')}
          subtitle={t('Truck, driver, trailer, and planned dates for this shipment.')}
        />
      </Box>

      <Box mt={5}>
        <ListingLoaCard
          listingId={listingId}
          loa={listing.loa}
          title={t('Letter of Attorney')}
          description={t('Generated automatically when the transport plan is confirmed.')}
        />
      </Box>

      <Box mt={5}>
        <Text fontSize="md" fontWeight="800" mb={4} color={titleColor}>
          {t('Transport Documents')}
        </Text>
        <Stack spacing={4}>
          <Box>
            <Text fontSize="xs" fontWeight="800" color={mutedText} textTransform="uppercase" mb={2}>
              {t('Proof of Pickup')}
            </Text>
            <DocumentList entityType="listing" entityId={listingId} docType="proof_pickup" refreshTrigger={refreshTrigger} />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="800" color={mutedText} textTransform="uppercase" mb={2}>
              {t('Final Documents')}
            </Text>
            <DocumentList entityType="listing" entityId={listingId} docType="cmr" refreshTrigger={refreshTrigger} />
            <Box mt={3}>
              <DocumentList entityType="listing" entityId={listingId} docType="proof_delivery" refreshTrigger={refreshTrigger} />
            </Box>
          </Box>
        </Stack>
      </Box>

      <Modal isOpen={isPlanOpen} onClose={closePlan} isCentered size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader fontWeight="800">
            {listing.status === 'planned' || listing.status === 'in_transit' ? t('Update transport plan') : t('Plan transport')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <Stack spacing={4}>
              <Text fontSize="sm" color={mutedText}>
                {t('Confirm the exact fleet and dates before the listing moves to Planned.')}
              </Text>
              <Box>
                <Text fontSize="xs" fontWeight="800" color={mutedText} mb={1}>{t('Truck')}</Text>
                <Select value={planTruckId} onChange={(e) => setPlanTruckId(e.target.value)} borderRadius="16px" isDisabled={loadingTrucks}>
                  <option value="">{loadingTrucks ? t('Loading approved trucks...') : t('Select approved truck')}</option>
                  {approvedTrucks.map((truck) => (
                    <option key={truck._id} value={truck._id}>
                      {[truck.licensePlate, truck.carCapacity ? `${truck.carCapacity} cars` : null, truck.maxLoadKg ? `${truck.maxLoadKg} kg` : null].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="800" color={mutedText} mb={1}>{t('Driver')}</Text>
                <Select value={planDriverId} onChange={(e) => setPlanDriverId(e.target.value)} borderRadius="16px" isDisabled={loadingDrivers}>
                  <option value="">{loadingDrivers ? t('Loading drivers...') : t('Select driver')}</option>
                  {drivers.map((driver) => (
                    <option key={driver._id} value={driver._id}>
                      {[driver.firstName, driver.lastName].filter(Boolean).join(' ')}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="800" color={mutedText} mb={1}>{t('Trailer (Optional)')}</Text>
                <Select value={planTrailerId} onChange={(e) => setPlanTrailerId(e.target.value)} borderRadius="16px" isDisabled={loadingTrailers}>
                  <option value="">{loadingTrailers ? t('Loading trailers...') : t('No trailer selected')}</option>
                  {trailers.map((trailer) => (
                    <option key={trailer._id} value={trailer._id}>
                      {[trailer.licensePlate, trailer.trailerCapacity ? `${trailer.trailerCapacity} cars` : null].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </Select>
              </Box>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <Box>
                  <Text fontSize="xs" fontWeight="800" color={mutedText} mb={1}>{t('Planned pickup date')}</Text>
                  <Input
                    type="date"
                    value={planPickupDate}
                    onChange={(e) => setPlanPickupDate(e.target.value)}
                    min={planPickupMin}
                    max={planPickupMax || undefined}
                    borderRadius="16px"
                  />
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="800" color={mutedText} mb={1}>{t('Planned delivery date')}</Text>
                  <Input
                    type="date"
                    value={planDeliveryDate}
                    onChange={(e) => setPlanDeliveryDate(e.target.value)}
                    min={planDeliveryMin}
                    max={planDeliveryMax || undefined}
                    borderRadius="16px"
                  />
                </Box>
              </Grid>

              <Box p={4} bg="blue.50" borderRadius="16px" border="1px solid" borderColor="blue.100" _dark={{ bg: 'blue.900', borderColor: 'blue.700' }}>
                <HStack spacing={2} mb={2}>
                  <Icon as={MdDescription} color="blue.500" boxSize={4} />
                  <Text fontSize="sm" fontWeight="700">{t('Purchase Terms')}</Text>
                </HStack>
                <Text fontSize="xs" color={mutedText} mb={3}>
                  {t('Download and review the purchase terms before confirming the transport plan.')}
                </Text>
                <Button
                  size="sm"
                  leftIcon={<MdDownload />}
                  variant="outline"
                  colorScheme="blue"
                  isLoading={purchaseTermsLoading}
                  isDisabled={!purchaseTermsDoc?.downloadUrl}
                  onClick={() => purchaseTermsDoc?.downloadUrl && window.open(purchaseTermsDoc.downloadUrl, '_blank', 'noopener,noreferrer')}
                  mb={3}
                >
                  {t('Download Purchase Terms')}
                </Button>
                <Checkbox
                  isChecked={hasAcceptedTerms}
                  onChange={(e) => setHasAcceptedTerms(e.target.checked)}
                  colorScheme="blue"
                  size="md"
                >
                  <Text fontSize="sm">{t('I have read and agree to the purchase terms for this transport job.')}</Text>
                </Checkbox>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" borderRadius="xl" onClick={closePlan}>{t('Cancel')}</Button>
            <Button
              colorScheme="blue"
              borderRadius="xl"
              isLoading={marking}
              isDisabled={!hasAcceptedTerms}
              onClick={handlePlanTransport}
            >
              {t('Confirm Transport Plan')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPickupOpen} onClose={closePickup} isCentered size="md">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader fontWeight="800">{t('Upload proof of pickup')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <Text fontSize="sm" color={mutedText} mb={4}>
              {t('Upload at least one pickup proof before the listing can move to Picked Up.')}
            </Text>
            <DocumentUploader
              ref={pickupUploaderRef}
              entityType="listing"
              entityId={listingId}
              docType="proof_pickup"
              accept=".jpg,.jpeg,.png,.pdf"
              multiple
              hideUploadButton
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" borderRadius="xl" onClick={closePickup}>
              {t('Cancel')}
            </Button>
            <Button
              colorScheme="blue"
              borderRadius="xl"
              isLoading={marking}
              onClick={confirmPickup}
            >
              {t('Upload & Mark Picked Up')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeliveredOpen} onClose={closeDelivered} isCentered size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader fontWeight="800">{t('Upload final documents')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={2}>
            <Stack spacing={5}>
              <Text fontSize="sm" color={mutedText}>
                {t('Upload a CMR or delivery photos before the listing can move to Delivered.')}
              </Text>
              <Box>
                <Text fontSize="xs" fontWeight="800" color={mutedText} textTransform="uppercase" mb={2}>
                  CMR
                </Text>
                <DocumentUploader
                  ref={cmrUploaderRef}
                  entityType="listing"
                  entityId={listingId}
                  docType="cmr"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  hideUploadButton
                />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="800" color={mutedText} textTransform="uppercase" mb={2}>
                  {t('Delivery photos')}
                </Text>
                <DocumentUploader
                  ref={deliveryUploaderRef}
                  entityType="listing"
                  entityId={listingId}
                  docType="proof_delivery"
                  accept=".jpg,.jpeg,.png,.pdf"
                  multiple
                  hideUploadButton
                />
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" borderRadius="xl" onClick={closeDelivered}>
              {t('Cancel')}
            </Button>
            <Button colorScheme="green" borderRadius="xl" isLoading={marking} onClick={confirmDelivered}>
              {t('Upload & Mark Delivered')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </UnifiedSurfaceCard>
  );
}

export default function PartnerListingDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { tabListProps, tabProps } = useUnifiedTabsStyles();
  const { user } = useAuth();
  const toast = useToast();
  const releaseRef = useRef(null);

  const [listing, setListing] = useState(null);
  const [myQuote, setMyQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [watched, setWatched] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const { isOpen: isReleaseOpen, onOpen: openRelease, onClose: closeRelease } = useDisclosure();

  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const titleColor = useColorModeValue('navy.800', 'white');
  const panelBorder = useColorModeValue('rgba(255,255,255,0.95)', 'rgba(255,255,255,0.16)');
  const subtleBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const subtleBorder = useColorModeValue('blue.100', 'whiteAlpha.200');

  const handleToggleWatch = async () => {
    setToggling(true);
    try {
      const result = await toggleWatch(id);
      setWatched(result.watched);
      toast({
        title: result.watched ? t('Added to watchlist') : t('Removed from watchlist'),
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch {
      toast({ title: t('Failed to update watchlist'), status: 'error', duration: 3000, isClosable: true });
    } finally {
      setToggling(false);
    }
  };

  const handleRelease = async () => {
    setReleasing(true);
    try {
      await releaseListing(id);
      toast({ title: t('Listing released. Your score has been penalised.'), status: 'warning', duration: 4000, isClosable: true });
      closeRelease();
      navigate('/search');
    } catch (err) {
      toast({ title: err?.response?.data?.message || t('Failed to release listing'), status: 'error', duration: 3000, isClosable: true });
    } finally {
      setReleasing(false);
    }
  };

  const loadListing = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getListingById(id);
      const nextListing = data?.listing ?? data;
      setListing(nextListing);
      setMyQuote(data?.myQuote ?? nextListing?.myQuote ?? null);
      setWatched(Boolean(data?.isWatched ?? nextListing?.isWatched));
    } catch {
      setListing(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadListing();
  }, [loadListing]);

  const MESSAGING_STATUSES = useMemo(() => new Set(['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed']), []);

  useEffect(() => {
    if (!listing || !user?.uid || !MESSAGING_STATUSES.has(String(listing.status || ''))) return;
    const lid = listing._id || listing.id;
    getUnreadMessageCount(lid, user.uid).then(setMessageUnreadCount).catch(() => {});
  }, [listing, user?.uid, MESSAGING_STATUSES]);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('tab') !== 'messages') return;
    if (!listing || !MESSAGING_STATUSES.has(String(listing.status || ''))) return;
    setTabIndex(2);
    searchParams.delete('tab');
    setSearchParams(searchParams, { replace: true });
  }, [listing, searchParams, setSearchParams, MESSAGING_STATUSES]);

  const detailRows = useMemo(() => {
    if (!listing) return [];
    return [
      { label: t('Reference'), value: listing.humanId || listing.reference },
      { label: t('Category'), value: t(listing.category || 'other') },
      { label: t('Client Price'), value: formatMoney(listing.clientPriceGross) },
      { label: t('Quote mode'), value: listing.allowsQuotes === false ? t('Fixed price only') : t('Partner quotes allowed') },
      { label: t('Pickup window'), value: formatDateRange(listing.pickupDateFrom, listing.pickupDateTo) },
      { label: t('Delivery window'), value: formatDateRange(listing.deliveryDateFrom, listing.deliveryDateTo) },
      { label: t('Partner pickup'), value: listing.partnerPickupDate ? fmt(listing.partnerPickupDate) : null },
      { label: t('Partner delivery'), value: listing.partnerDeliveryDate ? fmt(listing.partnerDeliveryDate) : null },
      { label: t('Distance'), value: formatDistanceKm(listing.distanceKm ?? listing.route?.distanceKm) },
      { label: t('Client type'), value: listing.clientType === 'company' ? t('Company') : t('Private Person') },
    ].filter((row) => row.value);
  }, [listing, t]);

  if (loading) {
    return (
      <PageShell>
        <Flex justify="center" align="center" minH="40vh">
          <Spinner size="lg" color="blue.400" />
        </Flex>
      </PageShell>
    );
  }

  if (!listing) {
    return (
      <PageShell>
        <Text color="red.400">{t('Listing not found.')}</Text>
      </PageShell>
    );
  }

  const listingId = listing._id || listing.id;
  const remaining = daysLeft(listing.expiresAt);
  const pickupLabel = [listing.fromAddress, listing.fromCity, listing.fromPostcode, listing.fromCountry].filter(Boolean).join(', ');
  const deliveryLabel = [listing.toAddress, listing.toCity, listing.toPostcode, listing.toCountry].filter(Boolean).join(', ');
  const mapOrigin = listing.fromCoords || listing.publicFromCoords || null;
  const mapDestination = listing.toCoords || listing.publicToCoords || null;
  const isAssignedPartner = listing.assignedPartnerId && user?.uid === listing.assignedPartnerId;
  const TRANSPORT_STATUSES = ['accepted', 'paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'];
  const showTransportCard = isAssignedPartner && TRANSPORT_STATUSES.includes(listing.status);
  const listingTitle = mapTemplateTitleToTranslation(listing.title, t) || listing.title || t('Untitled Listing');

  return (
    <PageShell>
      <Box w="100%" mx="auto">
        <Flex align="center" justify="space-between" mb={4}>
          <Button size="sm" variant="ghost" leftIcon={<Icon as={MdArrowBack} />} onClick={() => navigate(-1)}>
            {t('Back to search')}
          </Button>
          <HStack spacing={3}>
            <Button
              leftIcon={<Icon as={watched ? MdStar : MdStarBorder} />}
              variant={watched ? 'solid' : 'outline'}
              colorScheme={watched ? 'orange' : 'blue'}
              borderRadius="14px"
              size="sm"
              isLoading={toggling}
              onClick={handleToggleWatch}
            >
              {watched ? t('Watching') : t('Watch')}
            </Button>
            {remaining != null ? (
              <Box
                px={3}
                py={1.5}
                borderRadius="14px"
                bg={remaining <= 3 ? 'red.50' : 'blue.50'}
                color={remaining <= 3 ? 'red.600' : 'blue.600'}
                fontSize="sm"
                fontWeight="800"
                border="1px solid"
                borderColor={remaining <= 3 ? 'red.100' : 'blue.100'}
              >
                {remaining > 0
                  ? t('{{count}} day(s) left', { count: remaining })
                  : `${t('Expired')} ${fmt(listing.expiresAt)}`}
              </Box>
            ) : null}
            {isAssignedPartner && ['accepted', 'awaiting_payment'].includes(listing.status) && (
              <Button size="sm" colorScheme="orange" variant="outline" borderRadius="14px" onClick={openRelease}>
                {t('Release Listing')}
              </Button>
            )}
          </HStack>
        </Flex>

        <PageHeader
          title={listingTitle}
          description={t('Reference number', { id: listing.humanId || listing.reference })}
          actions={
            <AiAssistantDrawer
              role="partner"
              screenContext={{
                routeKey: 'partner.marketplace.detail',
                listingId: id,
                entityType: 'listing',
                entityId: id,
                role: 'partner',
                surface: 'marketplace-detail',
              }}
              buttonLabel={t('Ask AI')}
              subtitle={t('Prepare a quote or ask for the next lane action, then confirm before anything is submitted.')}
            />
          }
        />

        <UnifiedSurfaceCard mb={6} p={4} mt={6}>
          <HStack spacing={4} flexWrap="wrap">
            <Badge colorScheme="blue" textTransform="capitalize" borderRadius="full" px={4} py={1.5}>
              {t(listing.category || 'other')}
            </Badge>
            {listing.allowsQuotes === false ? (
              <Badge colorScheme="purple" borderRadius="full" px={4} py={1.5}>
                {t('Fixed price')}
              </Badge>
            ) : null}
            <StatusBadge status={listing.status} px={4} py={1.5} translation={t} />
            <Text fontSize="sm" color={mutedText} fontWeight="600">
            {t('Listed {{date}}', { date: fmt(listing.createdAt) })}
            </Text>
          </HStack>
        </UnifiedSurfaceCard>

        <Tabs index={tabIndex} onChange={setTabIndex} variant="unstyled">
          <Box
            mb={5}
            bg="whiteAlpha.400"
            border="1px solid"
            borderColor={panelBorder}
            borderRadius="18px"
            px={{ base: 3, md: 4 }}
            py={2}
            backdropFilter="blur(10px)"
          >
            <TabList {...tabListProps}>
              <Tab {...tabProps}>{t('Details')}</Tab>
              <Tab {...tabProps}>{t('Market Activity')}</Tab>
              {['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'].includes(String(listing.status || '')) ? (
                <Tab {...tabProps}>
                  {t('Messages')}
                  {messageUnreadCount > 0 ? (
                    <Box
                      as="span"
                      ml={2}
                      w="8px"
                      h="8px"
                      bg="blue.500"
                      borderRadius="full"
                      display="inline-block"
                    />
                  ) : null}
                </Tab>
              ) : null}
            </TabList>
          </Box>

          <TabPanels>
            <TabPanel p={0}>
              <Grid templateColumns={{ base: '1fr', xl: '1fr 390px' }} gap={6}>
                <GridItem>
                  <UnifiedSurfaceCard
                    p={{ base: 5, md: 6 }}
                  >
                    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} mb={5}>
                      <Box
                        p={5}
                        borderRadius="20px"
                        bg={subtleBg}
                        border="1px solid"
                        borderColor={subtleBorder}
                      >
                        <HStack spacing={2} mb={2}>
                          <Flex
                            w="36px"
                            h="36px"
                            borderRadius="16px"
                            align="center"
                            justify="center"
                            bg="whiteAlpha.700"
                            color="blue.500"
                            boxShadow="0 8px 18px rgba(15, 23, 42, 0.08)"
                          >
                            <Icon as={FaMapMarkerAlt} boxSize={5} />
                          </Flex>
                          <Text fontSize="10px" fontWeight="800" color="blue.600" textTransform="uppercase" letterSpacing="0.05em">
                            {t('Pick-up')}
                          </Text>
                        </HStack>
                        <Text fontWeight="800" color={titleColor} fontSize="md">
                          {pickupLabel || '—'}
                        </Text>
                        <Text fontSize="sm" color={mutedText} fontWeight="600" mt={1}>
                          {formatDateRange(listing.pickupDateFrom, listing.pickupDateTo)}
                        </Text>
                      </Box>
                      <Box
                        p={5}
                        borderRadius="20px"
                        bg={subtleBg}
                        border="1px solid"
                        borderColor={subtleBorder}
                      >
                        <HStack spacing={2} mb={2}>
                          <Flex
                            w="36px"
                            h="36px"
                            borderRadius="16px"
                            align="center"
                            justify="center"
                            bg="whiteAlpha.700"
                            color="blue.500"
                            boxShadow="0 8px 18px rgba(15, 23, 42, 0.08)"
                          >
                            <Icon as={FaMapMarkerAlt} boxSize={5} color="red.500" />
                          </Flex>
                          <Text fontSize="10px" fontWeight="800" color="blue.600" textTransform="uppercase" letterSpacing="0.05em">
                            {t('Delivery')}
                          </Text>
                        </HStack>
                        <Text fontWeight="800" color={titleColor} fontSize="md">
                          {deliveryLabel || '—'}
                        </Text>
                        <Text fontSize="sm" color={mutedText} fontWeight="600" mt={1}>
                          {formatDateRange(listing.deliveryDateFrom, listing.deliveryDateTo)}
                        </Text>
                      </Box>
                    </Grid>

                    <Box h={{ base: '240px', md: '320px' }} borderRadius="22px" overflow="hidden" mb={6} bg="gray.100">
                      {mapOrigin || mapDestination ? (
                        <Map
                          origin={mapOrigin}
                          destination={mapDestination}
                          center={{ lat: 50.5, lng: 10.5 }}
                          zoom={6}
                          width={800}
                          height={400}
                          fitBoundsPadding={72}
                          maxAutoZoom={12}
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                        />
                      ) : (
                        <Flex h="100%" align="center" justify="center" px={6} textAlign="center">
                          <Text color={mutedColor} fontSize="sm">
                            {t('A route preview appears once map coordinates are available.')}
                          </Text>
                        </Flex>
                      )}
                    </Box>

                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                      {detailRows.map((row) => (
                        <Box key={row.label}>
                          <Text fontSize="10px" fontWeight="800" color={mutedText} textTransform="uppercase" mb={1} letterSpacing="0.05em">
                            {row.label}
                          </Text>
                          <Text fontWeight="700" color={titleColor} fontSize="sm">
                            {row.value}
                          </Text>
                        </Box>
                      ))}
                    </Grid>

                    {listing.items?.length ? (
                      <>
                        <Divider my={6} />
                        <ListingItemsSection
                          items={listing.items}
                          category={listing.category}
                          subtitle={t('Quantities and handling details visible at a glance.')}
                        />
                      </>
                    ) : null}

                    <Divider my={6} />
                      <ListingImageGallery
                        images={listing.images}
                        title={t('Shipment images')}
                        emptyText={t('The client has not uploaded any shipment images.')}
                      />

                    {listing.description ? (
                      <>
                        <Divider my={6} />
                        <Box>
                          <Text fontSize="10px" fontWeight="800" color={mutedText} textTransform="uppercase" mb={1} letterSpacing="0.05em">
                            {t('Description')}
                          </Text>
                          <Text color={titleColor} fontSize="sm" lineHeight="tall">{listing.description}</Text>
                        </Box>
                      </>
                    ) : null}
                  </UnifiedSurfaceCard>
                </GridItem>

                <GridItem>
                  {showTransportCard ? (
                    <>
                      <TransportCard listing={listing} onStatusChanged={loadListing} />
                    </>
                  ) : (
                    listing.allowsQuotes === false
                      ? <FixedPriceAcceptPanel listing={listing} onAccepted={loadListing} />
                      : <QuotePanel listing={listing} listingId={listingId} myQuote={myQuote} onQuoteChanged={loadListing} />
                  )}
                </GridItem>
              </Grid>
            </TabPanel>

            <TabPanel p={0}>
              <VStack align="stretch" spacing={4}>
                <UnifiedSurfaceCard p={5}>
                  <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap">
                    <Box>
                      <Text fontWeight="800" color={titleColor} fontSize="md">
                        {t('Quotes on this listing')}
                      </Text>
                      <Text fontSize="sm" color={mutedText}>
                        {t('Refresh to check the latest market activity.')}
                      </Text>
                    </Box>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      borderRadius="12px"
                      size="sm"
                      leftIcon={<Icon as={MdRefresh} />}
                      onClick={loadListing}
                    >
                      {t('Refresh')}
                    </Button>
                  </Flex>
                </UnifiedSurfaceCard>
                <QuotesTab listingId={listingId} />
              </VStack>
            </TabPanel>

            {['paid', 'planned', 'in_transit', 'pickedUp', 'delivered', 'completed'].includes(String(listing.status || '')) ? (
              <TabPanel p={0}>
                <ListingMessagesPanel listingId={listingId} currentUserId={user?.uid} role="partner" onUnreadCountChange={setMessageUnreadCount} />
              </TabPanel>
            ) : null}
          </TabPanels>
        </Tabs>
      </Box>

      <AlertDialog isOpen={isReleaseOpen} leastDestructiveRef={releaseRef} onClose={closeRelease} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">{t('Release Listing?')}</AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>{t('You are about to release this listing back to the marketplace.')}</Text>
            <Text fontWeight="700" color="orange.500">{t('This will negatively affect your reliability score.')}</Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={releaseRef} onClick={closeRelease} isDisabled={releasing}>{t('Keep it')}</Button>
            <Button colorScheme="orange" ml={3} isLoading={releasing} onClick={handleRelease}>{t('Release')}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
